import { useCallback, useEffect, useMemo } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router';

import { PayflowInitializer } from '@ctct/application-layer';
import { RatingCallRequest, PriceVO } from '@ctct/domain-layer';
import type { AccountModificationFlowId, RatingPayload } from '@ctct/infrastructure-layer';
import { MainContentSpinner } from '@ctct/presentation-layer';
import {
  isStripeParamEnabled,
  PayflowTopBarWidget,
  SessionStorageKeys,
  usePayflowTopBarWidget,
  useSessionStorageZustandBeforeUnload,
} from '@ctct/react-app-layer';
import type { IPayflowPageStore } from '@ctct/state-client-layer';
import {
  createOrderSummaryHooks,
  createPayflowTopBarHooks,
  PaymentMethodStripeEditState,
  usePayflowPageStore,
} from '@ctct/state-client-layer';
import { useAccountId, useIs3PLCustomer, useIsSuspendedFacade } from '@ctct/state-server-layer';

import { CheckoutPage } from './CheckoutPage';
import { ConfirmationPage } from './ConfirmationPage';

import './payflow.scss';

// Define the serialized type for session storage
type SerializedPayflowPageStore = Omit<IPayflowPageStore, 'ratingPayload' | 'planBaseMonthlyPrice'> & {
  ratingPayload: RatingPayload | null;
  planBaseMonthlyPrice: null | {
    value: number;
    currency: string;
  };
};

// Enable inboundPromoCodes for secure app since it uses useInboundPromoCodesWidget
const initializer = new PayflowInitializer({ inboundPromoCodes: true });

const payflowTopBarHooks = createPayflowTopBarHooks(usePayflowPageStore);
const orderSummaryHooks = createOrderSummaryHooks(usePayflowPageStore);

const externalHooks = {
  useReturnUrl: payflowTopBarHooks.useReturnUrl,
  useReturnLabel: payflowTopBarHooks.useReturnLabel,
  useIsXeroAddonSelected: orderSummaryHooks.useIsXeroAddonSelected,
};

export const Checkout = ({ flowId }: { flowId: AccountModificationFlowId }) => {
  const isStripeEnabled = isStripeParamEnabled();
  const { data: accountId } = useAccountId();

  const { path } = useRouteMatch();
  const { is3PLCustomer: isCustomer, isLoading: is3PLCustomerLoading } = useIs3PLCustomer();
  const { data: isSuspendedData, isLoading: isLoadingIsSuspended } = useIsSuspendedFacade();
  const isSuspended = !isLoadingIsSuspended && isSuspendedData;

  useEffect(() => {
    initializer.listeners.on();

    return () => initializer.listeners.off();
  }, []);

  const transformSaveState = useCallback(
    (state: IPayflowPageStore): SerializedPayflowPageStore => ({
      ...state,
      alerts: [],
      promoCodeFormErrors: [],
      planBaseMonthlyPrice: state.planBaseMonthlyPrice
        ? { ...state.planBaseMonthlyPrice, value: state.planBaseMonthlyPrice.value.priceAmount }
        : null,
      smsOptionsDialogOpen: false,
      paymentMethodsFullLoading: false,
      paymentMethodsExternalRedirectLoading: false,
      ratingPayload: state.ratingPayload?.toJson() ?? null,
      ...(isStripeEnabled
        ? {
            paypalAgreementId: null,
            paymentMethodStripeEditState: PaymentMethodStripeEditState.Editable,
          }
        : null),
    }),
    [isStripeEnabled]
  );

  const transformLoadState = useCallback(
    (state: SerializedPayflowPageStore): IPayflowPageStore => ({
      ...state,
      planBaseMonthlyPrice: state.planBaseMonthlyPrice
        ? { ...state.planBaseMonthlyPrice, value: new PriceVO(state.planBaseMonthlyPrice.value) }
        : null,
      paymentMethodsExternalRedirectLoading: false,
      ratingPayload: state.ratingPayload ? RatingCallRequest.fromJson(state.ratingPayload) : null,
    }),
    []
  );

  const sessionStorageKey = useMemo(() => {
    if (isSuspended) {
      return SessionStorageKeys.payflow_v2_suspended_zustand_store;
    }

    if (isCustomer) {
      return SessionStorageKeys.payflow_v2_customer_zustand_store;
    }

    return SessionStorageKeys.payflow_v2_trialer_zustand_store;
  }, [isSuspended, isCustomer]);

  useSessionStorageZustandBeforeUnload(
    sessionStorageKey,
    accountId,
    usePayflowPageStore,
    transformSaveState,
    transformLoadState
  );

  const payflowTopBarWidget = usePayflowTopBarWidget({
    initializer,
    pageStore: usePayflowPageStore,
    flags: {
      isDesktopExitHidden: true,
    },
  });

  if (is3PLCustomerLoading || isLoadingIsSuspended) {
    return <MainContentSpinner />;
  }

  return (
    <>
      <PayflowTopBarWidget {...payflowTopBarWidget} />
      <Switch>
        <Route exact path={`${path}/checkout`}>
          <CheckoutPage initializer={initializer} flowId={flowId} />
        </Route>
        <Route exact path={`${path}/confirmation`}>
          <ConfirmationPage initializer={initializer} externalHooks={externalHooks} />
        </Route>
      </Switch>
    </>
  );
};
